import isNil from 'lodash/isNil';
import omitBy from 'lodash/omitBy';
import pickBy from 'lodash/pickBy';
import startCase from 'lodash/startCase';

import JsonSchema, { JsonSchemaBasicObject } from '@evidentid/json-schema/interfaces/JsonSchema';
import {
    ComplianceStatus,
    EvaluationResult,
} from '@evidentid/tprm-portal-lib/models/entity-details';
import { EntityRequirement } from '@evidentid/tprm-portal-lib/models/entity-details/EntityRequirement.model';

import { SpecialCombinedTprmFieldName } from '@/modules/entity-details/models/SpecialCombinedTprmFieldName.model';
import { SpecialCombinedTprmFieldTypeName } from '@/modules/entity-details/models/SpecialCombinedTprmFieldTypeName.model';
import { getCoverageAddressObjectString } from '@/modules/entity-details/utils/getCoverageAddressObjectString';

export function isEmptyValue(value: any): boolean {
    return isNil(value) ||
        (
            typeof value === 'object' &&
            Object.values(value).every((x) => isNil(x))
        );
}

export function isNonCompliantField(fieldName: string, evaluationResults: EvaluationResult[]): boolean {
    return evaluationResults.some(
        (result) =>
            result.usedFields?.some((usedField) => usedField.split('/').pop() === fieldName) &&
            result.result === ComplianceStatus.nonCompliant,
    );
}

export function shouldBeOmitted(key: string, value: any, evaluationResults: EvaluationResult[]): boolean {
    const notEvaluatedEmpty = !isNonCompliantField(key, evaluationResults) && isEmptyValue(value);
    const isSpecialCombinedTypeField = Object.values(
        SpecialCombinedTprmFieldTypeName,
    ).includes(key as SpecialCombinedTprmFieldTypeName);
    return key === 'coverageStatus' || notEvaluatedEmpty || isSpecialCombinedTypeField;
}

/**
 * Transforms the value of a special combined field based on its type field.
 * If the type is 'BLANKET', the value should be displayed as 'Blanket' instead of the actual value.
 * The value is wrapped in an array to maintain compatibility with JsonSchemaArrayView.
 * Otherwise, the original value is returned.
 */
export function transformSpecialCombinedFieldValue(
    fieldName: string,
    fieldValue: any,
    endorsements: Record<string, any>,
): any {
    if (fieldName === SpecialCombinedTprmFieldName.additionalInsured) {
        const typeValue = endorsements[SpecialCombinedTprmFieldTypeName.additionalInsuredType];
        if (typeValue === 'BLANKET') {
            return [ startCase(typeValue.toLowerCase()) ];
        }
    }
    if (fieldName === SpecialCombinedTprmFieldName.waiverOfSubrogationCompanyName) {
        const typeValue = endorsements[SpecialCombinedTprmFieldTypeName.waiverOfSubrogationCompanyNameType];
        if (typeValue === 'BLANKET') {
            return [ startCase(typeValue.toLowerCase()) ];
        }
    }
    return fieldValue;
}

export function getProducerInfo(
    requirement: EntityRequirement,
): { title: string, value: string | null }[] {
    return [
        {
            title: 'Producer Business Name',
            value: requirement.details.producerName || null,
        },
        { title: 'Producer Contact Name', value: requirement.details.producerContactName || null },
        { title: 'Producer Email', value: requirement.details.producerEmail || null },
        { title: 'Producer Telephone', value: requirement.details.producerTelephone || null },
        {
            title: 'Producer Address',
            value: getCoverageAddressObjectString(requirement.details.producerAddress) || null,
        },
    ];
}

export function isMultiPropsField(property: { [key: string]: JsonSchema } | {}): boolean {
    return 'properties' in property;
}

export function omitMultiPropFieldsFromSchema(schema: JsonSchemaBasicObject): JsonSchemaBasicObject {
    return {
        ...schema,
        properties: 'properties' in schema
            ? omitBy(schema.properties, isMultiPropsField)
            : {},
    };
}

export function pickMultiPropFieldsFromSchema(schema: JsonSchemaBasicObject): JsonSchemaBasicObject {
    return {
        ...schema,
        properties: 'properties' in schema
            ? pickBy(schema.properties, isMultiPropsField)
            : {},
    };
}
